import { axiosFetch as authFetch } from './axiosFetch';
/**
 * Reward Catalog API Service
 * Base URL: REACT_APP_API_ENDPOINT_LOYALTY
 * All endpoints: /api/v1/loyalty/rewards/*
 *
 * One dollar-off reward per program, created at program setup only.
 * Supported operations: list, get, update, toggle.
 */

import type { Reward } from '../components/loyalty/data/types';

const LOYALTY_BASE = process.env.REACT_APP_API_ENDPOINT_LOYALTY || 'http://localhost:4004';

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const stored = localStorage.getItem('CREDENTIALS');
    if (stored) {
      const creds = JSON.parse(stored);
      const token = creds?.accessToken || creds?.access_token;
      if (token && typeof token === 'string' && token.trim()) {
        headers['Authorization'] = `Bearer ${token.trim()}`;
      }
      const merchantId = creds?.merchantId;
      if (merchantId && typeof merchantId === 'string' && merchantId.trim()) {
        headers['x-brand-id'] = merchantId.trim();
      }
    }
  } catch {
    // ignore parse errors
  }
  return headers;
}

async function handleResponse<T = any>(response: Response): Promise<T> {
  const text = await response.text();
  let json: any;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response: ${text.substring(0, 200)}`);
  }
  if (!response.ok) {
    const errorMessage = json?.error?.message || json?.message || json?.error || `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }
  if (json.success === false) {
    const errorMessage = json?.error?.message || json?.message || 'Request failed';
    throw new Error(errorMessage);
  }
  return json;
}

// ─── Payload Types ──────────────────────────────────────────────

export interface UpdateRewardPayload {
  name?: string;
  points_cost?: number;
  value_per_points?: number;
  max_discount_cap?: number | null;
  delivery_time?: 'sameday' | 'daybefore' | 'weekbefore' | null;
  grace_period?: number | null;
}

export interface ListRewardsParams {
  isautomatedreward?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface RewardsListResponse {
  success: boolean;
  data: {
    rewards: Reward[];
  };
}

export interface RewardDetailResponse {
  success: boolean;
  data: Reward;
}

// ─── API: List Rewards ──────────────────────────────────────────
export const listRewards = async (params?: ListRewardsParams): Promise<RewardsListResponse> => {
  const query = new URLSearchParams();
  if (params?.isautomatedreward !== undefined) query.set('isautomatedreward', String(params.isautomatedreward));
  if (params?.sort) query.set('sort', params.sort);
  if (params?.order) query.set('order', params.order);
  const qs = query.toString();
  const response = await authFetch(`${LOYALTY_BASE}/api/v1/loyalty/rewards${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<RewardsListResponse>(response);
};

// ─── API: Get Reward Detail ─────────────────────────────────────
export const getRewardDetail = async (rewardId: string): Promise<RewardDetailResponse> => {
  const response = await authFetch(`${LOYALTY_BASE}/api/v1/loyalty/rewards/${rewardId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<RewardDetailResponse>(response);
};

// ─── API: Update Reward ─────────────────────────────────────────
export const updateReward = async (rewardId: string, payload: UpdateRewardPayload): Promise<RewardDetailResponse> => {
  const response = await authFetch(`${LOYALTY_BASE}/api/v1/loyalty/rewards/${rewardId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse<RewardDetailResponse>(response);
};

